import { canAddToDynamicPortfolio, isWeightTotalComplete, sumWeights } from "@repo/shared";
import type { DynamicPortfolio, ProductListItem, WrapperType } from "@repo/shared/types";
import { useMemo } from "react";
import {
  useAddDynamicPortfolioProduct,
  useDeleteDynamicPortfolioProduct,
  useUpdateDynamicPortfolioProduct,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";

import {
  ProductsWeightsEditor,
  productOption,
  type WeightEditorRow,
  wrapperNamesOf,
} from "../../portfolio-weights";
import { T } from "./dynamic-portfolio-products-editor.i18n";
import { PreferenceTiltSettings } from "./preference-tilt-settings";

type DynamicPortfolioProductsEditorProps = {
  collectionId: string;
  portfolio: DynamicPortfolio;
  /** The collection's product register. */
  products: ProductListItem[];
  /** Collection wrapper types; empty when the feature is disabled (hides the column). */
  wrapperTypes: WrapperType[];
  /** The collection has client preferences, so the tilt maximums apply. */
  showPreferenceTilt: boolean;
};

/** The products and weights of one dynamic portfolio, edited as a model portfolio's are. */
export function DynamicPortfolioProductsEditor({
  collectionId,
  portfolio,
  products,
  wrapperTypes,
  showPreferenceTilt,
}: DynamicPortfolioProductsEditorProps) {
  const localize = useLocalize();
  const t = useT(T);

  const addProduct = useAddDynamicPortfolioProduct();
  const updateProduct = useUpdateDynamicPortfolioProduct();
  const deleteProduct = useDeleteDynamicPortfolioProduct();

  const rows: WeightEditorRow[] = useMemo(() => {
    const productsById = new Map(products.map((product) => [product.id, product]));
    return portfolio.products.map((row) => {
      const product = productsById.get(row.productId);
      return {
        id: row.id,
        productId: row.productId,
        name: localize(row.productNameI18n),
        isin: row.isin,
        weight: row.weight,
        minBuyAmount: product?.minBuyAmount ?? null,
        wrapperNames: wrapperNamesOf(product, wrapperTypes, localize),
      };
    });
  }, [portfolio.products, products, wrapperTypes, localize]);

  // What the server accepts and the portfolio does not hold yet.
  const productOptions = useMemo(
    () =>
      products
        .filter(
          (product) =>
            canAddToDynamicPortfolio(product, portfolio.platformIds) &&
            !portfolio.products.some((row) => row.productId === product.id),
        )
        .map((product) => productOption(product, localize)),
    [products, portfolio.products, portfolio.platformIds, localize],
  );

  const weights = portfolio.products.map((row) => row.weight);

  return (
    <ProductsWeightsEditor
      backTo={`/settings/portfolios/${collectionId}/dynamic-portfolios`}
      backLabel={t.back}
      heading={t.heading(String(portfolio.riskScore))}
      settings={
        showPreferenceTilt && (
          <PreferenceTiltSettings collectionId={collectionId} portfolio={portfolio} />
        )
      }
      rows={rows}
      productOptions={productOptions}
      showWrapperTypes={wrapperTypes.length > 0}
      totalWeight={sumWeights(weights)}
      isComplete={isWeightTotalComplete(weights)}
      weightStep="0.0001"
      onAdd={(productId, weight) =>
        addProduct.mutateAsync({
          collectionId,
          dynamicPortfolioId: portfolio.id,
          data: { productId, weight },
        })
      }
      onUpdate={(dynamicPortfolioProductId, weight) =>
        updateProduct.mutateAsync({
          collectionId,
          dynamicPortfolioId: portfolio.id,
          dynamicPortfolioProductId,
          data: { weight },
        })
      }
      onRemove={(dynamicPortfolioProductId) =>
        deleteProduct.mutateAsync({
          collectionId,
          dynamicPortfolioId: portfolio.id,
          dynamicPortfolioProductId,
        })
      }
    />
  );
}
